
import { defineComponent, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Tooltip from 'primevue/tooltip';
import type { FormActionStatus, DetectedConsent, CustomSchemaAction } from '../../composables/template-editor/FormActionsConfig';
import { getPropertyDisplayName } from '../../composables/template-editor/FormActionsEvaluator';
import { isPlatformWithoutPassword, hasPasswordFieldOnTemplate } from '../../composables/template-editor/useShopifyPasswordNotice';

interface MarketingChannel {
  channelId: string;
  label: string;
  state: 'ok' | 'warning' | 'inactive' | 'inherited';
  tooltip: string | null;
}

interface TransactionalChannel {
  id: string;
  label: string;
  active: boolean;
  inherited: boolean;
}

export default defineComponent({
  name: 'FormActionCard',
  directives: {
    tooltip: Tooltip,
  },

  props: {
    actionStatus: {
      type: Object as () => FormActionStatus,
      required: true,
    },
    submissionOption: {
      type: Object as () => { kind: 'allowResubmit' | 'allowResubmitData'; value: boolean } | null,
      default: null,
    },
  },

  emits: ['toggle-action', 'toggle-submission-option', 'toggle-tracking-consent', 'add-disclosure-widget'],

  setup(props) {
    const { t } = useI18n();

    const isConsentAction = computed(() => props.actionStatus.action.id === 'consent');
    const isCustomSchemaAction = computed(() => props.actionStatus.action.id.startsWith('custom_data_storage_'));
    const trackingConsent = computed(() => props.actionStatus.trackingConsent);

    const submissionOptionLabel = computed(() => {
      if (!props.submissionOption) return '';
      return props.submissionOption.kind === 'allowResubmitData'
        ? t('templateBuilder.formFields.allowResubmitDataLabel')
        : t('templateBuilder.formFields.allowResubmitLabel');
    });

    // Surfaces a warning on the "Création de compte" action card when the
    // platform handles authentication itself (e.g. Shopify) but the form still
    // collects a password — that password will be silently ignored at submit.
    const showShopifyPasswordWarning = computed(() => (
      props.actionStatus.action.id === 'account_creation'
      && props.actionStatus.isEnabled
      && isPlatformWithoutPassword()
      && hasPasswordFieldOnTemplate()
    ));

    const schemaId = computed(() => {
      if (!isCustomSchemaAction.value) return null;
      return (props.actionStatus.action as CustomSchemaAction).schemaId || null;
    });

    const schemaDescription = computed(() => {
      if (!isCustomSchemaAction.value) return null;
      return (props.actionStatus.action as CustomSchemaAction).schemaDescription || null;
    });

    const cardStateClass = computed(() => {
      if (props.actionStatus.isEnabled) return 'state-enabled';
      if (props.actionStatus.isAvailable) return 'state-available';
      return 'state-unavailable';
    });

    const iconBadgeStyle = computed(() => {
      const color = props.actionStatus.action.color || '#6c757d';
      if (props.actionStatus.isEnabled) {
        return { backgroundColor: `${color}14`, color };
      }
      return {};
    });

    const hasContent = computed(() => {
      if (isConsentAction.value && props.actionStatus.detectedConsents?.length) return true;
      if (isCustomSchemaAction.value && props.actionStatus.customPropertiesBySchema) return true;
      if (props.actionStatus.availableFields.length > 0) return true;
      if (props.actionStatus.optionalAvailableFields.length > 0) return true;
      if (props.actionStatus.missingFields.length > 0) return true;
      return false;
    });

    const flatCustomProperties = computed(() => {
      if (!props.actionStatus.customPropertiesBySchema) return [];
      return Object.values(props.actionStatus.customPropertiesBySchema).flat();
    });

    // --- Consent: Marketing channels ---
    const getMarketingState = (consent: DetectedConsent): 'ok' | 'warning' | 'inactive' => {
      if (!consent.identifierPresent) return 'inactive';
      if (consent.mode === 'not_configured') return 'warning';
      if (consent.mode === 'implicit' && consent.checkboxRequiredByAccountCreation) return 'warning';
      return 'ok';
    };

    const getMarketingTooltip = (consent: DetectedConsent): string | null => {
      if (!consent.identifierPresent) return null;

      if (consent.mode === 'not_configured' && consent.channelId === 'whatsapp') {
        return t('templateBuilder.fields.formActions.consent.tooltipWhatsappCheckboxRequired');
      }

      if (consent.checkboxRequiredByAccountCreation) {
        return t('templateBuilder.fields.formActions.consent.tooltipCheckboxRequiredByAccountCreation', { channel: consent.label });
      }

      return null;
    };

    const marketingChannels = computed((): MarketingChannel[] => {
      if (!props.actionStatus.detectedConsents) return [];
      return props.actionStatus.detectedConsents.map((consent) => {
        if (consent.mode === 'already_collected') {
          return {
            channelId: consent.channelId,
            label: consent.label,
            state: 'inherited' as const,
            tooltip: null,
          };
        }
        return {
          channelId: consent.channelId,
          label: consent.label,
          state: getMarketingState(consent),
          tooltip: getMarketingTooltip(consent),
        };
      });
    });

    // --- Consent: Transactional channels ---
    const transactionalChannels = computed((): TransactionalChannel[] => {
      if (!props.actionStatus.detectedConsents) return [];
      const inheritedChannelIds = new Set(
        props.actionStatus.detectedConsents
          .filter((c) => c.mode === 'already_collected')
          .map((c) => c.channelId),
      );
      const hasEmail = props.actionStatus.detectedConsents.some((c) => c.channelId === 'email' && c.identifierPresent);
      const hasPhone = props.actionStatus.detectedConsents.some((c) => (c.channelId === 'sms' || c.channelId === 'whatsapp') && c.identifierPresent);
      return [
        { id: 'email', label: t('templateBuilder.fields.formActions.consent.transactional.email'), active: hasEmail, inherited: inheritedChannelIds.has('email') },
        { id: 'sms', label: t('templateBuilder.fields.formActions.consent.transactional.sms'), active: hasPhone, inherited: inheritedChannelIds.has('sms') },
        { id: 'whatsapp', label: t('templateBuilder.fields.formActions.consent.transactional.whatsapp'), active: hasPhone, inherited: inheritedChannelIds.has('whatsapp') },
      ];
    });

    const hasDisclosureAdvisory = computed(() => {
      if (!props.actionStatus.detectedConsents) return false;
      // Only show advisory if at least one channel has marketing actively OK (not just warning)
      const hasActiveMarketingOk = props.actionStatus.detectedConsents.some(
        (c) => c.mode !== 'already_collected' && getMarketingState(c) === 'ok',
      );
      if (!hasActiveMarketingOk) return false;
      return props.actionStatus.detectedConsents.some(
        (c) => c.disclosureAdvisory && c.mode !== 'already_collected',
      );
    });

    const channelStateIcon = (state: string): string => {
      switch (state) {
        case 'ok': return 'fas fa-check-circle';
        case 'warning': return 'fas fa-exclamation-circle';
        case 'inactive': return 'fas fa-minus-circle';
        case 'inherited': return 'fas fa-link';
        default: return 'fas fa-circle';
      }
    };

    return {
      t,
      getPropertyDisplayName,
      isConsentAction,
      isCustomSchemaAction,
      trackingConsent,
      submissionOptionLabel,
      schemaId,
      schemaDescription,
      cardStateClass,
      iconBadgeStyle,
      hasContent,
      flatCustomProperties,
      marketingChannels,
      transactionalChannels,
      hasDisclosureAdvisory,
      channelStateIcon,
      showShopifyPasswordWarning,
    };
  },
});
